import { Component, Input, OnDestroy } from '@angular/core';
import { NbWindowRef } from '@nebular/theme';
import { Apollo } from 'apollo-angular';
import { SubSink } from 'subsink';
import { WindowService } from '../../../../@core/services/window.service';
import { JournalLineDetailComponent } from '../journal-line-detail/journal-line-detail.component';
import { EquipmentDetailComponent } from '../../../equipment/detail/equipmentdetail.component';
import { JournalDetailsComponent } from '../journal-details/journal-details.component';
import { GET_SUM_EQUIPMENT_COUNTING_JOURNAL_LINE_BOOK_VALUE } from '../../../../@core/graphql/queries/equipment-counting-journal/getSumEquipmentCountingJournalLineBookValue.query';
import { MetricAggregation } from '../../models/metricAggregation.enum';

export interface MetricDetailLinesContext {
  object: string;
  filters: { column: string; value: string }[];
  title?: string;
  showDetailOnDoubleClick?: boolean;
  customDoubleClickHandler?: (rowData: any) => void;
  detailType?: 'equipment' | 'journal' | 'journalLine';
}

export interface QueueKpiCard {
  queueName: string;
  queueId: string;
  bookValueSum: number;
  isLoading: boolean;
  filter: { column: string; value: string }[];
}

@Component({
    selector: 'stack-mono-metric-detail-lines',
    templateUrl: './metric-detail-lines.component.html',
    styleUrls: ['./metric-detail-lines.component.scss'],
    standalone: false
})
export class MetricDetailLinesComponent implements OnDestroy {
  @Input() object = 'EquipmentCountingJournalLine';
  @Input() filters: { column: string; value: string }[] = [];
  @Input() title = '';
  @Input() showDetailOnDoubleClick = false;
  @Input() detailType: 'equipment' | 'journal' | 'journalLine' = 'journalLine';
  @Input() hideQueues = true;
  @Input() showKpiCards = true;
  @Input() page = '';

  // More Details button support - allows parent to inject a callback for showing a detail breakdown modal
  @Input() showMoreDetailsButton = false;
  @Input() moreDetailsHandler: (() => void) | null = null;

  // Store queues data when loaded from dynamic list view
  queues: any[] = [];

  // KPI cards data for each queue
  queueKpiCards: QueueKpiCard[] = [];

  // Track which queue IDs have already been loaded to prevent duplicate fetches
  private loadedQueueIds: Set<string> = new Set();

  private subs = new SubSink();

  constructor(
    private windowRef: NbWindowRef,
    private windowService: WindowService,
    private apollo: Apollo,
  ) {}

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Handle queues loaded event from dynamic list view
   * Fetches the sum of equipmentBookValue for each queue
   * Prevents duplicate fetches by tracking already loaded queue IDs
   */
  onQueuesLoaded(queues: any[]): void {
    this.queues = queues;
    if (this.showKpiCards && queues && queues?.length > 0) {
      // Check if we've already loaded these exact queues
      const currentQueueIds = queues
        .map((q) => q.id)
        .sort((a, b) => String(a).localeCompare(String(b)))
        .join(',');
      const loadedQueueIds = Array.from(this.loadedQueueIds)
        .sort((a, b) => String(a).localeCompare(String(b)))
        .join(',');

      if (currentQueueIds === loadedQueueIds && this.queueKpiCards.length > 0) {
        // Already loaded these queues, skip fetching
        return;
      }

      this.loadKpiCardsForQueues(queues);
    }
  }

  /**
   * Load KPI cards for all queues by fetching the sum of equipmentBookValue
   */
  private loadKpiCardsForQueues(queues: any[]): void {
    // Clear previously loaded queue IDs and update with new ones
    this.loadedQueueIds.clear();
    queues.forEach((queue) => this.loadedQueueIds.add(queue.id));

    // Initialize KPI cards with loading state
    this.queueKpiCards = queues.map((queue) => ({
      queueName: queue.name || 'Unnamed Queue',
      queueId: queue.id,
      bookValueSum: 0,
      isLoading: true,
      filter: queue.filter || [],
    }));

    // Fetch sum for each queue
    queues.forEach((queue, index) => {
      this.fetchBookValueSumForQueue(queue, index);
    });
  }

  /**
   * Fetch the sum of equipmentBookValue for a specific queue
   * Now uses the aggregation fields in the filter for flexibility
   */
  private fetchBookValueSumForQueue(queue: any, index: number): void {
    const filter = queue.filter || [];

    this.subs.sink = this.apollo
      .query<{ getSumEquipmentCountingJournalLineBookValue: number }>({
        query: GET_SUM_EQUIPMENT_COUNTING_JOURNAL_LINE_BOOK_VALUE,
        variables: {
          search: {
            filter: filter,
            first: 1,
            aggregate: MetricAggregation.Sum,
            aggregateField: 'equipmentBookValue',
          },
        },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: (result) => {
          if (this.queueKpiCards[index]) {
            this.queueKpiCards[index].bookValueSum =
              result.data?.getSumEquipmentCountingJournalLineBookValue || 0;
            this.queueKpiCards[index].isLoading = false;
          }
        },
        error: (error) => {
          console.error(
            `Error fetching book value sum for queue ${queue.name}:`,
            error,
          );
          if (this.queueKpiCards[index]) {
            this.queueKpiCards[index].isLoading = false;
          }
        },
      });
  }

  /**
   * Generic method to fetch aggregated metrics for a specific queue
   * This demonstrates how to use different aggregation types and fields
   */
  private fetchAggregatedMetricForQueue(
    queue: any,
    index: number,
    aggregationType: MetricAggregation,
    fieldName: string,
  ): void {
    const filter = queue.filter || [];

    this.subs.sink = this.apollo
      .query<{ getSumEquipmentCountingJournalLineBookValue: number }>({
        query: GET_SUM_EQUIPMENT_COUNTING_JOURNAL_LINE_BOOK_VALUE,
        variables: {
          search: {
            filter: filter,
            first: 1,
            aggregate: aggregationType,
            aggregateField: fieldName,
          },
        },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: (result) => {
          if (this.queueKpiCards[index]) {
            this.queueKpiCards[index].bookValueSum =
              result.data?.getSumEquipmentCountingJournalLineBookValue || 0;
            this.queueKpiCards[index].isLoading = false;
          }
        },
        error: (error) => {
          console.error(
            `Error fetching ${aggregationType} of ${fieldName} for queue ${queue.name}:`,
            error,
          );
          if (this.queueKpiCards[index]) {
            this.queueKpiCards[index].isLoading = false;
          }
        },
      });
  }

  /**
   * Format currency value for display (full format)
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  /**
   * Format currency value in compact notation (e.g., $360K, $1.2M)
   */
  formatCurrencyCompact(value: number): string {
    if (value >= 1_000_000_000) {
      return '$' + (value / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    } else if (value >= 1_000_000) {
      return '$' + (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (value >= 1_000) {
      return '$' + (value / 1_000).toFixed(0) + 'K';
    }
    return '$' + value.toFixed(0);
  }

  /**
   * Handle double-click on a row in the dynamic list view
   * Opens the appropriate detail component based on detailType
   */
  onRowDoubleClick(rowData: any): void {
    switch (this.detailType) {
      case 'equipment':
        this.openEquipmentDetail(rowData);
        break;
      case 'journal':
        this.openJournalDetail(rowData);
        break;
      case 'journalLine':
      default:
        this.openJournalLineDetail(rowData);
        break;
    }
  }

  /**
   * Open equipment detail page
   */
  private openEquipmentDetail(rowData: any): void {
    const equipmentId =
      rowData.equipmentId || rowData.equipment?.id || rowData.id;
    if (!equipmentId) {
      console.warn('Equipment ID not found in row data');
      return;
    }

    this.windowService.openWindow(EquipmentDetailComponent, {
      objectId: equipmentId,
      context: { id: equipmentId },
      options: {
        title: `Equipment - ${rowData.equipmentNum || rowData.equipment?.equipmentNum || equipmentId}`,
        windowClass: 'detail-window',
      },
    });
  }

  /**
   * Open journal detail page
   */
  private openJournalDetail(rowData: any): void {
    this.windowService.openWindow(JournalDetailsComponent, {
      context: {
        journalId: rowData.id,
        journalName: rowData.name || rowData.id,
        journalStatus: rowData.status,
      },
      objectId: rowData.id,
      options: {
        title: `Journal Details - ${rowData.name || rowData.id}`,
        windowClass: 'detail-window',
      },
    });
  }

  /**
   * Open journal line detail page
   */
  private openJournalLineDetail(rowData: any): void {
    this.windowService.openWindow(JournalLineDetailComponent, {
      context: {
        lineData: rowData,
      },
      objectId: rowData.id,
      options: {
        title: `Journal Line - ${rowData.equipmentNum || rowData.equipment?.equipmentNum || rowData.id}`,
        windowClass: 'detail-window',
      },
    });
  }

  /**
   * Handle "More Details" button click
   */
  onMoreDetailsClick(): void {
    if (this.moreDetailsHandler) {
      this.moreDetailsHandler();
    }
  }

  /**
   * Close the window
   */
  close(): void {
    this.windowRef?.close();
  }
}
